import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TTDCampaign } from '@feathr/blackbox';
import { Tooltip, UniversalCreatableSelect } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

interface IProps {
  campaign: TTDCampaign;
}

interface IOption {
  value: string;
  label: string;
}

export const PERIOD_MAX = 2147483647;
export const PERIOD_MIN = 1;

function compareOption(inputValue: string, option: IOption): boolean {
  return inputValue === option.value;
}

function isValidNewOption(
  inputValue: string,
  _: IOption | IOption[] | null,
  options: readonly IOption[],
): boolean {
  const asNumber = Number(inputValue);
  return !(
    !inputValue ||
    !Number.isFinite(asNumber) ||
    !(asNumber > 0 && asNumber <= PERIOD_MAX) ||
    options.some((option) => compareOption(inputValue, option))
  );
}

function FreqPeriodSelect({ campaign }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const period = campaign.get('bidding_strategy').freq_period;

  function createOption(inputValue: string): IOption {
    const asNumeral = numeral(Number(inputValue));
    return {
      value: asNumeral.format('0'),
      label: t('{{count}} hour', { count: Number(inputValue) }),
    };
  }

  const onChangeFreqPeriod = (value: string | number): void => {
    let newPeriod;
    if (value === 'lifetime') {
      newPeriod = PERIOD_MAX;
    } else {
      newPeriod = Number(value) * 60;
    }
    campaign.set({
      bidding_strategy: {
        ...campaign.get('bidding_strategy'),
        freq_period: newPeriod > PERIOD_MAX ? PERIOD_MAX : newPeriod,
      },
    });
  };

  function handleSelectSingle(option: IOption): void {
    onChangeFreqPeriod(option.value);
  }

  const lifeTimeOption: IOption = { value: 'lifetime', label: t('Lifetime') };
  const defaultOptions: IOption[] = [
    { value: '1', label: t('{{count}} hour', { count: 1 }) },
    { value: '8', label: t('{{count}} hour', { count: 8 }) },
    { value: '24', label: t('{{count}} hour', { count: 24 }) },
    lifeTimeOption,
  ];

  function formatCreateLabel(inputValue: string): string {
    return t('Use custom frequency period: {{count}} hour', {
      count: Number(inputValue),
    });
  }

  // TODO: add tests for this component when we're not responding to deployment blockers
  function getValue(period: number): IOption {
    if (period < PERIOD_MIN) {
      return defaultOptions[0];
    }

    if (period === PERIOD_MAX) {
      return lifeTimeOption;
    }

    if (period > PERIOD_MAX) {
      return lifeTimeOption;
    }

    return {
      value: String(Math.ceil(period / 60)),
      label: t('{{count}} hour', {
        count: Math.ceil(period / 60),
      }),
    };
  }

  const label = (
    <>
      {t('Frequency period')}{' '}
      <Tooltip
        title={t(
          "The period of time after which a person's frequency cap is reset. Choosing 'Lifetime' means the frequency cap is the maximum number of impressions per person.",
        )}
      >
        {getIconForAction('info')}
      </Tooltip>
    </>
  );

  return (
    <UniversalCreatableSelect<IOption>
      createOption={createOption}
      defaultOptions={defaultOptions}
      formatCreateLabel={formatCreateLabel}
      isFullWidth={true}
      isValidNewOption={isValidNewOption}
      label={label}
      name={'frequency-period'}
      onSelectSingle={handleSelectSingle}
      value={getValue(period)}
    />
  );
}

export default observer(FreqPeriodSelect);
